import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
import stripeConfig from '../config/stripe';
import { collection, query, where, or, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { calculateClientAlerts, getEffectiveThresholds } from '../utils/alertUtils';
import { Box, Typography, Tabs, Tab, CircularProgress, Alert, Button, Chip, Paper } from '@mui/material';
import { SwapHoriz as SwapHorizIcon, Notifications as NotificationsIcon } from '@mui/icons-material';
// NEW: Import the required Firebase Auth functions
import { getAuth, signOut } from 'firebase/auth';
import QuestionsSection from './QuestionsSection';
import ManageClientsPage from './ManageClientsPage';
import UserPortalAIGovernance from './UserPortalAIGovernance';
import AccountPage from '../pages/AccountPage';
import useOrganizationConfig from '../hooks/useOrganizationConfig'; // NEW: Import org config hook
import SupportModal from './SupportModal';
import SetupWizardModal from './SetupWizard/SetupWizardModal';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useParams } from 'react-router-dom';


const ClientDashboard = () => {
const { user, userData, loading: authLoading, refreshUser, isOrgAdmin, userPortalAccess } = useAuth();
const { config: orgConfig, loading: configLoading } = useOrganizationConfig(user?.organizationId); // NEW: Load org config
const [rawClients, setRawClients] = useState([]); // State for raw data from Firestore
const [clients, setClients] = useState([]); // Final state for UI with all enhancements
const [loadingClients, setLoadingClients] = useState(true);
const [error, setError] = useState(null);
const navigate = useNavigate();
  const { tab } = useParams();

  // Map tab names to indices
  const tabMap = {
    'insights': 0,
    'manage': 1,
    'ai-governance': 2,
    'account': 3
  };

  // Set initial tab based on URL parameter
  const initialTab = tab ? (tabMap[tab] ?? 0) : 0;
const [activeTab, setActiveTab] = useState(initialTab);
const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
const [selectedClientId, setSelectedClientId] = useState(null);
const [asanaConnection, setAsanaConnection] = useState(null);
const [isAsanaLoading, setIsAsanaLoading] = useState(true);
const [firmOrgExists, setFirmOrgExists] = useState(false);

// Setup Wizard Modal state
const [wizardOpen, setWizardOpen] = useState(false);
const [wizardClientId, setWizardClientId] = useState(null);

// Alert state
const [totalAlertCount, setTotalAlertCount] = useState(0);
const [firstAlertingClientId, setFirstAlertingClientId] = useState(null);
const [userAlertSettings, setUserAlertSettings] = useState({
  strategicContextDays: 60,
  financialsDays: 90,
  asanaDays: 30
});
const [dismissedAlerts, setDismissedAlerts] = useState({});

const handleClientSelect = (clientId) => {
  console.log(`[DEBUG] ClientDashboard: Client selected: ${clientId}`);
  setSelectedClientId(clientId);
};

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Update URL when tab changes
    const tabNames = ['insights', 'manage', 'ai-governance', 'account'];
    navigate(`/client-dashboard/${tabNames[newValue]}`, { replace: true });
  };

  const handleNavigateToTab = (tabIndex) => {
    handleTabChange(null, tabIndex);
};

  // Effect 1: Real-time listener for RAW client data from Firestore
useEffect(() => {
    if (!user?.uid) {
        setRawClients([]);
        setLoadingClients(false);
        return;
    }
    setLoadingClients(true);
    setError(null);
    console.log(`[DEBUG] Setting up real-time listener for user ID: ${user.uid}`);
    const clientsRef = collection(db, 'clients');
    const q = query(clientsRef, or(
        where('userId', '==', user.uid),
        where('fcfoId', '==', user.uid)
    ));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const clientsData = [];
        querySnapshot.forEach((doc) => {
            clientsData.push({ id: doc.id, ...doc.data(), asanaConnection: null });
        });
        console.log(`[DEBUG] Real-time update: Found ${clientsData.length} raw clients from Firestore.`);
        setRawClients(clientsData); // Instantly update state with core data
        setLoadingClients(false);
    }, (err) => {
        console.error("Error with real-time client listener: ", err);
        setError("Failed to listen for client data updates.");
        setLoadingClients(false);
        toast.error("Connection to client list failed.");
    });
    return () => {
        console.log("[DEBUG] Detaching real-time client listener.");
        unsubscribe();
    };
}, [user?.uid]);

// Effect 2: Progressive enhancement with Asana data
useEffect(() => {
    if (rawClients.length === 0) {
        setClients([]);
        return;
    }
    const fetchAsanaStatuses = async () => {
        console.log(`[DEBUG] Enhancing ${rawClients.length} clients with Asana status.`);
        const clientsWithAsanaStatus = await Promise.all(
            rawClients.map(async (client) => {
                try {
                    const integrationRef = doc(db, `clients/${client.id}/integrations/asana`);
                    const integrationSnap = await getDoc(integrationRef);
                    if (integrationSnap.exists()) {
                        return { ...client, asanaConnection: integrationSnap.data() };
                    }
                } catch (e) {
                    console.error(`Failed to fetch Asana status for client ${client.id}`, e);
                }
                return { ...client, asanaConnection: null };
            })
        );
        setClients(clientsWithAsanaStatus);
    };
    fetchAsanaStatuses();
}, [rawClients]);

// Listen for Asana integration status
useEffect(() => {
  if (!user?.uid) {
    setIsAsanaLoading(false);
    return;
  }
  const integrationRef = doc(db, 'users', user.uid, 'integrations', 'asana');
  const unsubscribe = onSnapshot(integrationRef, (docSnap) => {
    if (docSnap.exists()) {
      setAsanaConnection(docSnap.data());
    } else {
      setAsanaConnection(null);
    }
    setIsAsanaLoading(false);
  }, (error) => {
    console.error("Error fetching Asana connection status in Dashboard:", error);
    toast.error("Could not load Asana integration status.");
    setIsAsanaLoading(false);
  });
  return () => unsubscribe();
}, [user?.uid]);

// Load user alert settings and dismissed alerts
useEffect(() => {
  const loadUserAlertData = async () => {
    if (!user?.uid) return;
    
    try {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const data = userSnap.data();
        if (data.alertSettings) {
          setUserAlertSettings(prev => ({ ...prev, ...data.alertSettings }));
        }
        if (data.dismissedAlerts) {
          setDismissedAlerts(data.dismissedAlerts);
        }
      }
    } catch (error) {
      console.error('Error loading user alert data:', error);
    }
  };
  
  loadUserAlertData();
}, [user?.uid]);

// Calculate alerts across all clients
  useEffect(() => {
    if (!clients || clients.length === 0) {
      setTotalAlertCount(0);
      setFirstAlertingClientId(null);
      return;
    }

    const thresholds = getEffectiveThresholds({}, userAlertSettings);
    let totalCount = 0;
    let firstClientWithAlert = null;

    clients
      .filter(c => c.isActive === true)
      .forEach(client => {
        const clientAlertResult = calculateClientAlerts(client, thresholds, dismissedAlerts);
        
        if (clientAlertResult.hasAlerts) {
          totalCount += clientAlertResult.alerts.length;
          if (!firstClientWithAlert) {
            firstClientWithAlert = client.id;
          }
        }
      });

    setTotalAlertCount(totalCount);
    setFirstAlertingClientId(firstClientWithAlert);
  }, [clients, userAlertSettings, dismissedAlerts]);

// Save portal preference for dual-access users
useEffect(() => {
    if (isOrgAdmin && user?.userType !== 'Firm Portal Admin') {
        localStorage.setItem('lastPortalView', 'client-dashboard');
    }
}, [isOrgAdmin, user?.userType]);

// Check if firm portal org actually exists (in case it was deleted)
useEffect(() => {
    const checkOrgExists = async () => {
        if (user?.organizationId && isOrgAdmin) {
            try {
                const orgRef = doc(db, 'organizations', user.organizationId);
                const orgSnap = await getDoc(orgRef);
                setFirmOrgExists(orgSnap.exists());
            } catch (err) {
                console.error('Error checking org existence:', err);
                setFirmOrgExists(false);
            }
        } else {
            setFirmOrgExists(false);
        }
    };
    checkOrgExists();
}, [user?.organizationId, isOrgAdmin]);

   const handleDataChange = useCallback(async (options = {}) => {
  console.log('[DEBUG] A child component triggered a data refresh.');
  if (refreshUser) {
    await refreshUser();
    // Only show toast if not a silent refresh (e.g., setup completion has its own UI feedback)
    if (!options.silent) {
      toast.info("Your account information has been updated.");
    }
  }
}, [refreshUser]);

  // Setup Wizard handlers
  const openWizard = useCallback((clientId = null) => {
    setWizardClientId(clientId);
    setWizardOpen(true);
  }, []);

  const closeWizard = useCallback(() => {
    setWizardOpen(false);
    setWizardClientId(null);
  }, []);

  const handleWizardSetupComplete = useCallback((clientName) => {
    closeWizard();
    if (handleDataChange) {
      handleDataChange({ silent: true });
    }
    toast.success(`🎉 Setup complete for "${clientName}"! Your client is being activated.`);
  }, [closeWizard, handleDataChange]);

  // Find first client needing setup (for "Continue Setup →" link)
  const clientNeedingSetup = useMemo(() => {
    if (!Array.isArray(clients)) return null;
    return clients.find(c => c.isSetupComplete === false) || null;
  }, [clients]);

  const location = useLocation();
  console.log('[DIAGNOSTIC] ClientDashboard rendered. The location.state is:', location.state);

  // Auto-reopen wizard after QuickBooks OAuth redirect
  // The wizard saves 'wizardResumeClientId' to sessionStorage before redirecting to QB.
  // On return, we detect this flag + QB URL params and reopen the wizard.
  useEffect(() => {
    const resumeClientId = sessionStorage.getItem('wizardResumeClientId');
    if (resumeClientId) {
      const params = new URLSearchParams(window.location.search);
      const qbStatus = params.get('qb_status');

      if (qbStatus) {
        // QB OAuth returned — show toast based on status
        if (qbStatus === 'success') {
          toast.success(params.get('message') || 'QuickBooks connected successfully!');
        } else if (qbStatus === 'error') {
          toast.error(`QuickBooks error: ${params.get('message') || 'Unknown error'}`);
          // Clear the QB resume flag on error so wizard doesn't show stale state
          sessionStorage.removeItem('wizardResumeAfterQB');
        }

        // Clean QB params from URL
        params.delete('qb_status');
        params.delete('message');
        // Keep clientId param if present — it's used by other components
        const newSearch = params.toString();
        const newUrl = newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      }

      // Reopen wizard with the saved clientId
      // Small delay to let the client data listener update with QB status
      setTimeout(() => {
        openWizard(resumeClientId);
        sessionStorage.removeItem('wizardResumeClientId');
      }, 500);
    }
  }, []); // Run once on mount
// Effect to handle deep-linking from other pages (like InitialSetupPage)
useEffect(() => {
    // Check if we have navigation state from another page
    if (location.state && typeof location.state.targetTab === 'number') {
        const targetTabValue = location.state.targetTab;
        console.log(`[DEBUG] Navigation state received, setting active tab to index: ${targetTabValue}`);
        
        // Ensure the target tab is a valid index (0, 1, or 2)
         if (targetTabValue >= 0 && targetTabValue <= 3) {
            setActiveTab(targetTabValue);
        }
    }
}, [location.state]); // This effect runs when navigation state changes

  // NEW: A self-contained, working logout handler
  const handleLogout = async () => {
    try {
        const auth = getAuth();
        await signOut(auth);
        toast.info("You have been logged out.");
    } catch (error) {
        console.error("Error logging out:", error);
        toast.error("Failed to log out.");
    }
  };

  // Handler for resubscribing after trial expiration
  const handleResubscribe = async () => {
    try {
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      const result = await createCheckoutSession({ idempotencyKey: `resubscribe-${Date.now()}` });

      const sessionId = result.data?.sessionId;
      if (!sessionId) {
        throw new Error('Could not retrieve a checkout session ID.');
      }

      const stripe = await loadStripe(stripeConfig.publishableKey);
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error(error.message || 'Failed to start checkout. Please try again.');
    }
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Authenticating...</Typography>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">User session not found. Please log in.</Alert>
      </Box>
    );
  }

  // Trial-expired blocking screen
  if (userData?.stripeSubscriptionStatus === 'trial_expired') {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh', 
        bgcolor: 'grey.100',
        p: 3 
      }}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'warning.main' }}>
            ⏰ Trial Period Ended
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Your 14-day free trial has ended. To continue using SavvaiEdge and access your client insights, please activate your subscription.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your data is safe and will be available once you subscribe.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleResubscribe}
            >
              Subscribe Now - $95/month
            </Button>
            <Button
              variant="outlined"
              onClick={handleLogout}
            >
              Logout
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'grey.100' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: orgConfig?.primaryColor || 'primary.main',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Co-branded header: SavvaiEdge | Partner Logo */}
          <Typography variant="h5" component="h1" fontWeight="bold" sx={{ color: 'white' }}>
            SavvaiEdge
          </Typography>
          {orgConfig?.logoUrl && (
            <>
              <Box sx={{ mx: 2, height: 24, borderLeft: '1px solid rgba(255,255,255,0.5)' }} />
              <Box
                component="img"
                src={orgConfig.logoUrl}
                alt={orgConfig.organizationName || 'Logo'}
                sx={{ height: 36 }}
              />
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* View Switcher - Only show for dual-access users (have both portals) AND org exists */}
{isOrgAdmin && userPortalAccess && firmOrgExists && (
  <Chip
    icon={<SwapHorizIcon />}
    label="Switch to Firm Portal"
    onClick={() => navigate('/firm-portal')}
              sx={{ 
                mr: 2,
                cursor: 'pointer',
                bgcolor: 'rgba(255,255,255,0.15)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
              }}
            />
          )}
           {/* Alert Bell */}
          {totalAlertCount > 0 && (
            <Box
              onClick={() => {
                setActiveTab(1); // Navigate to Manage Clients tab
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                mr: 2,
                p: 1,
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <NotificationsIcon sx={{ color: 'white' }} />
              <Box
                sx={{
                  ml: 0.5,
                  bgcolor: 'error.main',
                  color: 'white',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}
              >
                {totalAlertCount > 9 ? '9+' : totalAlertCount}
              </Box>
            </Box>
          )}

          <Button 
            variant="outlined" 
            onClick={() => setIsSupportModalOpen(true)} 
            sx={{ 
              mr: 2, 
              color: 'white', 
              borderColor: 'rgba(255,255,255,0.5)',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            Contact Support
          </Button>
          <Button 
            variant="contained" 
            onClick={handleLogout}
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: { xs: 1, sm: 2, md: 3 } }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', alignItems: 'center' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="dashboard tabs" sx={{ flexGrow: 1 }}>
           <Tab label="Insights" />
           <Tab label="Manage Clients" />
           <Tab label="AI Governance" />
           <Tab label="Account" />
          </Tabs>
          {clientNeedingSetup && !wizardOpen && (
            <Button
              onClick={() => openWizard(clientNeedingSetup.id)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: 'primary.main',
                whiteSpace: 'nowrap',
                mr: 2,
                animation: 'subtle-pulse 2s ease-in-out infinite',
                '@keyframes subtle-pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.7 },
                },
              }}
            >
              Continue Setup →
            </Button>
          )}
        </Box>

        <Box sx={{ flexGrow: 1, mt: 3, overflowY: 'auto' }}>
          {loadingClients ? (
             <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading clients...</Typography>
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <>
               {activeTab === 0 && (
    <QuestionsSection
        user={user}
        userData={userData}
        clients={clients}
        selectedClientId={selectedClientId}
        onClientSelect={handleClientSelect}
        onQuestionSubmitted={handleDataChange}
        userAsanaConnection={asanaConnection}
        onNavigateToTab={handleNavigateToTab}
        orgConfig={orgConfig}
        configLoading={configLoading}
        onOpenWizard={openWizard}
    />
)}
              {activeTab === 1 && (
                <ManageClientsPage
                  user={user}
                  clients={clients}
                  onClientDataChange={handleDataChange}
                  initialState={location.state}
                  userAsanaConnection={asanaConnection}
                  onAlertDismissed={(clientId, alertType) => {
                    // Update local dismissed state to refresh badge count immediately
                    setDismissedAlerts(prev => ({
                      ...prev,
                      [clientId]: {
                        ...(prev[clientId] || {}),
                        [alertType]: new Date()
                      }
                    }));
                  }}
                />
              )}
              {activeTab === 2 && (
                <UserPortalAIGovernance
                  user={user}
                  clients={clients}
                />
              )}
              {activeTab === 3 && (
                  <AccountPage
    user={user}
    userData={userData}
    clients={clients}
    onClientDataChange={handleDataChange}
    asanaConnection={asanaConnection}
    isAsanaLoading={isAsanaLoading}
    onOpenWizard={openWizard}
/>
              )}
            </>
          )}
        </Box>
      </Box>
      <SupportModal open={isSupportModalOpen} onClose={() => setIsSupportModalOpen(false)} user={user} />
      <SetupWizardModal
        open={wizardOpen}
        onClose={closeWizard}
        clientId={wizardClientId}
        user={user}
        userData={userData}
        clients={clients}
        onClientDataChange={handleDataChange}
        onSetupComplete={handleWizardSetupComplete}
      />
    </Box>
  );
};

export default ClientDashboard;